import React, { useEffect, useMemo, useState } from 'react';
import { ErrorMessage, Field, Form, FormikProvider, useFormik } from 'formik';
import * as Yup from "yup";
import { config } from '../CommonUtils/CommonURLS';
import Swal from 'sweetalert2';
import { ImagePdfBucket } from '../CommonUtils/ImagesBase64';
import { CommonAxiosGet, CommonAxiosPost } from '../CommonUtils/CommonAxios';
import { viewImage } from '../CommonUtils/viewImage';
import { FaEye } from 'react-icons/fa';
import CommonReactTable from '../CommonUtils/CommonReactTable';

export default function Grievance() {
    const username = sessionStorage.getItem("user_name");
    const userId = sessionStorage.getItem("user_id");
    const loginType = sessionStorage.getItem("loginType");
    const role = loginType === 'college' ? 'college' : 'student';
    const isCollege = role === 'college';
    const centerTitle = isCollege ? 'College Grievance' : 'Student Grievance';
    const formTitle = isCollege ? 'Raise College Grievance' : 'Raise Student Grievance';
    const identityLabel = isCollege ? 'College Name' : 'Student Name';
    const detailsPlaceholder = isCollege
        ? 'Explain the college issue clearly.'
        : 'Explain the student issue clearly.';
    const [mode, setMode] = useState('raise');
    const [trackLoading, setTrackLoading] = useState(false);
    const [trackResults, setTrackResults] = useState([]);
    const [trackLoaded, setTrackLoaded] = useState(false);

    const formatDateOnly = (value) => {
        if (!value) return '-';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return date.toLocaleDateString('en-GB');
    };

    const buildUploadFileName = () => {
        const safeUser = (username || 'user').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
        const safeId = (userId || 'id').toString().replace(/[^a-zA-Z0-9_]/g, '');
        return `grievupload_${safeUser}_${safeId}`;
    };

    const validationSchema = Yup.object({
        prblmType: Yup.string().required("Please select Problem Type"),
        concern: Yup.string()
            .required("Concern is required")
            .min(10, "Minimum 10 characters required"),
        // mobileno: Yup.string()
        //     .required("Mobile number is required")
        //     .matches(/^(?!([0-9])\1{9}$)[6-9][0-9]{9}$/, "Enter valid 10 digit mobile number"),
        // captcha: Yup.string().required("Captcha is required")
    });

    const InstitutionFormik = useFormik({
        initialValues: {
            candidateId: "",
            concern: "",
            grievupload: "",
            prblmType: "",
            // mobileno: "",
            // captcha: "",
        },
        validationSchema,
        enableReinitialize: false,

        onSubmit: (values) => {

            // if (values.captcha !== captcha) {
            //     Swal.fire("Invalid Captcha", "", "error");
            //     return;
            // }

            Swal.fire({
                text: "Are you sure you want to submit?",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, submit it!",
                cancelButtonText: "No, cancel!",
                backdrop: false,
            }).then((result) => {
                if (result.isConfirmed) {
                    const url = config.url.STU_URL + "grievance";
                    const payload = {
                        ...values,
                        candidateId: userId,
                        grievanceFor: role,
                        userName: username,
                        universityId: loginType,
                        userId,

                    };

                    CommonAxiosPost(url, payload).then((res) => {
                        if (res?.data?.scode === "01") {
                            InstitutionFormik.resetForm();
                            // handleRefresh();
                            // successAlertRefresh(res?.data?.sdesc);
                        } else {
                            Swal.fire(res?.data?.sdesc || "Failed", "", "error");
                        }
                    });
                }
            });
        }
    });





    const handleModeChange = (selectedMode) => {
        setMode(selectedMode);
        InstitutionFormik.resetForm();
        // setCaptchaa(generateCaptcha());
    };

    const fetchTrackReport = async () => {
        setTrackLoading(true);
        setTrackLoaded(false);
        try {
            const url = config.url.STU_URL + "grievance-status";
            const res = await CommonAxiosGet(url);
            if (res.data?.scode === "01") {
                setTrackResults(res?.data?.data || []);
            } else {
                setTrackResults([]);
                Swal.fire(res?.data?.sdesc || "Failed to fetch grievance details", "", "error");
            }
        } catch (error) {
            setTrackResults([]);
            Swal.fire("Unable to fetch grievance details", "", "error");
        } finally {
            setTrackLoading(false);
            setTrackLoaded(true);
        }
    };

    useEffect(() => {
        if (mode === 'track') {
            fetchTrackReport();
        }
    }, [mode]);

    const trackColumns = useMemo(() => ([
        {
            id: 'slNo',
            header: 'SNo',
            cell: ({ row }) => row.index + 1,
            meta: { style: { textAlign: 'center' } }
        },
        {
            id: 'updated_time',
            header: 'Date',
            accessorKey: 'updated_time',
            cell: ({ row }) => formatDateOnly(row.original.updated_time)
        },
        {
            id: 'prblm_type',
            header: 'Problem Type',
            accessorKey: 'prblm_type'
        },
        {
            id: 'concern',
            header: 'Concern Description',
            accessorKey: 'concern'
        },
        {
            id: 'concern_doc',
            header: 'Concern Document',
            cell: ({ row }) => {
                const concernDoc = row.original.grievupload || "";
                return concernDoc ? (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => viewImage(concernDoc)}
                        title="View concern document"
                    >
                        <FaEye />
                    </button>
                ) : '-';
            },
            meta: { style: { textAlign: 'center' } }
        },
        {
            id: 'statusremarks',
            header: 'Remarks',
            accessorKey: 'status_remarks'
        },
        {
            id: 'support_doc',
            header: 'Support Document',
            cell: ({ row }) => {
                const supportDoc = row.original?.support_doc || ""
                return supportDoc ? (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => viewImage(supportDoc)}
                        title="View support document"
                    >
                        <FaEye />
                    </button>
                ) : '-';
            },
            meta: { style: { textAlign: 'center' } }
        },
        {
            id: 'status_desc',
            header: 'Status',
            accessorKey: 'status_desc',
            cell: ({ row }) => row.original.status_desc || "-"
        }
    ]), []);

    return (
        <>
            <div className={`grievance-page ${isCollege ? 'grievance-page--college' : 'grievance-page--student'}`}>
                <div className="grievance-top">
                    <h2 className="grievance-title">{centerTitle}</h2>
                </div>
                <div className="grievance-controls">
                    <label className={`grievance-control-label ${mode === 'raise' ? 'grievance-control-label--active' : ''}`}>
                        <input
                            type="radio"
                            value="raise"
                            checked={mode === 'raise'}
                            onChange={() => handleModeChange('raise')}
                        />
                        Raise a Concern
                    </label>
                    <label className={`grievance-control-label ${mode === 'track' ? 'grievance-control-label--active' : ''}`}>
                        <input
                            type="radio"
                            value="track"
                            checked={mode === 'track'}
                            onChange={() => handleModeChange('track')}
                        />
                        Track Your Concern
                    </label>
                    {/* <button type="button" onClick={() => navigate(-1)} className="btn btn-secondary">Back</button> */}
                </div>
                {mode === 'track' && (
                    <div className="grievance-note-card">
                        {/* <h4 className="card-title mb-2 text-primary">Track Your Concern</h4> */}
                        <div className="mt-3">
                            {trackLoading ? (
                                <div className="text-muted">Fetching details...</div>
                            ) : !trackLoaded ? null : trackResults.length === 0 ? (
                                <div className="text-muted">No grievance details found.</div>
                            ) : (
                                <CommonReactTable
                                    data={trackResults}
                                    columns={trackColumns}
                                    showFooter={false}
                                    showPagination={false}
                                    showExcel={true}
                                    showPrint={true}
                                    excelFileName="Grievance_Track_Report"
                                />
                            )}
                        </div>
                    </div>
                )}
                {mode === 'raise' && (
                    <div className="grievance-card">
                        <h2 className="grievance-form-title">{formTitle}</h2>
                        <FormikProvider value={InstitutionFormik}>
                            <Form onSubmit={InstitutionFormik.handleSubmit}>
                                <div className="row g-3 mb-1">
                                    <div className="col-12 col-md-12">
                                        <label className="grievance-field-label">{identityLabel}</label>
                                        <div className="grievance-info-box">
                                            <div className="grievance-info-value">{`${username || '-'} ${userId ? `| ${userId}` : ''}`}</div>
                                        </div>
                                    </div>

                                    {/* <div className="col-12 col-md-6">
                                        <label className="grievance-field-label">Enter Mobile No</label>
                                        <Field
                                            type="text"
                                            name="mobileno"
                                            className="form-control"
                                            maxLength="10"
                                        />
                                        <ErrorMessage name="mobileno" component="div" className="text-danger" />
                                    </div> */}
                                </div>
                                <div className="col-12 col-md-12">
                                    <label className="grievance-field-label">Type of Problem</label>
                                    <Field as="select" name="prblmType" className="form-control">
                                        <option value="">--Select--</option>
                                        <option value="Technical">Technical</option>
                                        <option value="Administrative">Administrative</option>
                                    </Field>
                                    <ErrorMessage name="prblmType" component="div" className="text-danger" />
                                </div>
                                <div className="mb-2">
                                    <label className="grievance-field-label">Enter Concern Details</label>
                                    <Field
                                        as="textarea"
                                        name="concern"
                                        className="form-control grievance-concern-textarea"
                                        placeholder={detailsPlaceholder}
                                    />
                                    <ErrorMessage name="concern" component="div" className="text-danger" />
                                </div>

                                <div className="row g-3 mb-1">
                                    <div className="col-12 col-md-12">
                                        <label className="grievance-field-label">Upload File</label>
                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="file"
                                                className="form-control"
                                                name="grievupload"
                                                accept="application/pdf"
                                                onChange={(e) => {
                                                    const uploadName = buildUploadFileName();
                                                    ImagePdfBucket(e, InstitutionFormik, "/Uploads/grievupload/", "grievupload", uploadName, "pdf").then((uploadedPath) => {
                                                        InstitutionFormik.setFieldValue("grievupload", uploadedPath || "");
                                                    });
                                                }}
                                            />
                                            {InstitutionFormik.values.grievupload && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => viewImage(InstitutionFormik.values.grievupload)}
                                                >
                                                    <FaEye />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {/* <div className="mb-2">
                                    <label className="grievance-field-label">Enter Captcha</label>

                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <div className="grievance-captcha-box">
                                            {captcha}
                                        </div>

                                        <button type="button" className="grievance-refresh-btn" onClick={handleRefresh} title="Refresh Captcha">
                                            <TfiReload size={16} />
                                        </button>
                                        <Field
                                            type="text"
                                            name="captcha"
                                            className="form-control mt-2"
                                            placeholder="Enter captcha"
                                        />
                                    </div>
                                    <ErrorMessage name="captcha" component="div" className="text-danger" />
                                </div> */}
                                <div className="grievance-action-row">
                                    <button type="submit" className="btn btn-primary grievance-submit-btn">
                                        Submit
                                    </button>
                                </div>

                            </Form>
                        </FormikProvider>
                    </div>
                )}
            </div>
        </>
    );
}